/**
 * Pages that used to be their own sidebar entries, regrouped as tabs of the
 * page they belong to. Each group owns one route; `?tab=` picks the view, and
 * the retired routes redirect into it so old links keep working.
 */
import { PageTabs, useTab, type PageTab } from '../components/PageTabs';
import { PharmacyFeesPage } from './PharmacyFeesPage';
import { ShippingFeesPage } from './ShippingFeesPage';
import { PharmacyVolumePage } from './PharmacyVolumePage';
import { PricingPage } from './PricingPage';
import { PriceCoveragePage } from './PriceCoveragePage';
import { PaymentsPage } from './PaymentsPage';
import { PaymentMethodsPage } from './PaymentMethodsPage';

/** Shipping bills on the pharmacy invoice and volume is its usage report, so
 *  all three are views of the same run rather than three destinations. */
export const PHARMACY_TABS: readonly PageTab[] = [
  { key: 'fees', label: 'Fees' },
  { key: 'shipping', label: 'Shipping' },
  { key: 'volume', label: 'Volume' },
];

export const PharmacyBillingPage = () => {
  const tab = useTab(PHARMACY_TABS);
  const tabs = <PageTabs tabs={PHARMACY_TABS} current={tab} />;
  if (tab === 'shipping') return <ShippingFeesPage tabs={tabs} />;
  if (tab === 'volume') return <PharmacyVolumePage tabs={tabs} />;
  return <PharmacyFeesPage tabs={tabs} />;
};

/** Coverage is the gap report for the very table Rates edits. */
export const PRICING_TABS: readonly PageTab[] = [
  { key: 'rates', label: 'Rates' },
  { key: 'coverage', label: 'Coverage' },
];

export const PricingGroupPage = () => {
  const tab = useTab(PRICING_TABS);
  const tabs = <PageTabs tabs={PRICING_TABS} current={tab} />;
  if (tab === 'coverage') return <PriceCoveragePage tabs={tabs} />;
  return <PricingPage tabs={tabs} />;
};

/** Cards on file are what the charge attempts run against. */
export const PAYMENTS_TABS: readonly PageTab[] = [
  { key: 'activity', label: 'Activity' },
  { key: 'methods', label: 'Methods' },
];

export const PaymentsGroupPage = () => {
  const tab = useTab(PAYMENTS_TABS);
  const tabs = <PageTabs tabs={PAYMENTS_TABS} current={tab} />;
  if (tab === 'methods') return <PaymentMethodsPage tabs={tabs} />;
  return <PaymentsPage tabs={tabs} />;
};

/** Every tabbed route and the views it hosts, for routing and its tests. */
export const TAB_GROUPS: Record<string, readonly PageTab[]> = {
  '/pharmacy': PHARMACY_TABS,
  '/pricing': PRICING_TABS,
  '/payments': PAYMENTS_TABS,
};
